import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { queryClient } from './lib/queryClient';
import { AppPages } from './app/routes';
import { OnboardingProvider } from './features/onboarding';
import { useSessionKeepAlive } from './hooks/useSessionKeepAlive';
import { useAccountLanguageSync } from './hooks/useAccountLanguageSync';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CookieBanner } from './common/widget/CookieBanner';
import { OfflineReadNotice } from './common/widget/OfflineReadNotice';
import { BackgroundSync } from './hooks/BackgroundSync';
import { NativeAppEvents } from './features/native-app/widget/NativeAppEvents';
import { AppUpdateRequired } from './features/native-app/widget/AppUpdateRequired';
import { TermsUpdateGate } from './features/legal/widget/TermsUpdateGate';

function SessionKeepAlive({ children }: { children: React.ReactNode }) {
  useSessionKeepAlive();
  return <>{children}</>;
}

function AccountLanguageSync() {
  useAccountLanguageSync();
  return null;
}

function AppShell() {
  return (
    <SessionKeepAlive>
      <OnboardingProvider>
        <ErrorBoundary>
          <AppPages />
        </ErrorBoundary>
        <Toaster richColors position="top-center" closeButton />
        <CookieBanner />
        <OfflineReadNotice />
        <BackgroundSync />
        <AccountLanguageSync />
        <TermsUpdateGate />
        <NativeAppEvents />
        <AppUpdateRequired />
      </OnboardingProvider>
    </SessionKeepAlive>
  );
}

// A data router only so that a page can hold a navigation (`useBlocker`, VOL-101): the
// pages stay declared as descendant <Routes> in AppPages, under this single catch-all.
const router = createBrowserRouter([{ path: '*', element: <AppShell /> }]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

export default App;
